/**
 * Campaign Setup — stand up a candidate and their whole staff in one go.
 *
 * Five steps assemble a DRAFT; nothing exists in the tenant until "Start
 * Campaign". The server does the same validation on commit that it does on
 * preflight, so this view never has to guess whether the button will work —
 * it asks, and disables the button until the answer is yes.
 *
 * Two things drive most of the design:
 *
 * 1. **Containment is the model.** A staffer's turf must sit inside the
 *    candidate's reach (`scope_is_subset` server-side, straddle-safe). The UI
 *    expresses turf relative to the campaign's district rather than in the
 *    abstract, so an out-of-district scope reads as "outside this campaign"
 *    rather than as a permissions error after the fact.
 *
 * 2. **Zero scopes means EVERYTHING.** An empty reach is tenant-global, not
 *    empty — which is why county-wide has to be confirmed with a checkbox
 *    rather than inferred from a blank field.
 *
 * Findings from the server carry a stable `code` and a `subject` (usually an
 * email), which is how they get attached to the row that caused them instead
 * of being pattern-matched out of prose.
 *
 * Styling follows the other admin views: semantic tokens only
 * (bg-bg-base/bg-bg-raised, text-fg-*, bg-accent, status-*).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Plus,
  Rocket,
  Route,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useMapRegions } from '@/context/MapRegionsContext';
import InviteLinkList, { InviteCsvButton } from '@/components/admin/InviteLinkList';
import { InfoTip } from '@/components/InfoTip';
import type { UserScopeInput } from '@/types';
import {
  abandonLaunchPlan,
  commitLaunchPlan,
  createLaunchPlan,
  buildStarterRoutes,
  fetchRoleOptions,
  fetchSeats,
  listLaunchPlans,
  preflightLaunchPlan,
  updateLaunchPlan,
  type LaunchCommitResult,
  type LaunchDraft,
  type LaunchPlan,
  type LaunchRosterMember,
  type PreflightReport,
  type RoleOption,
  type SeatSnapshot,
  type StarterRoute,
} from '@/api/launch';
import {
  ROSTER_TEMPLATES,
  applyRosterTemplate,
  describeShortfalls,
  type RosterTemplateId,
} from '@/lib/rosterTemplates';

/**
 * Coerce anything the API returns into something React can render.
 *
 * A 422 from FastAPI puts an ARRAY of validation-error objects in `detail`,
 * and dropping that straight into JSX throws "Objects are not valid as a
 * React child" — which replaces the real error with a stack trace and tells
 * the user nothing. Every catch here goes through this.
 */
function errorText(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (typeof detail?.message === 'string') return detail.message;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d: any) => (typeof d?.msg === 'string' ? d.msg : null))
      .filter(Boolean);
    if (msgs.length) return msgs.join('. ');
  }
  return fallback;
}

/**
 * What each role can actually DO, lifted from the backend `UserRole`
 * docstrings. Showing the capability rather than only the title is the
 * cheapest guard against the most consequential mistake here: over-granting
 * because the job title sounded right.
 */
const ROLE_BLURB: Record<string, string> = {
  admin: 'Everything, including user management and bulk export.',
  org_manager: 'All voters, routes, and bulk export. Cannot manage admins.',
  manager: 'Legacy alias for Org Manager. Prefer Org Manager for new accounts.',
  analyst: 'Aggregate analytics and turnout. No individual voter names.',
  coordinator: 'Plans routes and assigns canvassers, inside their turf only. No bulk export.',
  canvasser: 'Only the voters on routes assigned to them. Logs knocks.',
  viewer: 'Read-only aggregate maps. No individual voter names.',
  messenger: 'Sends texts one at a time. Cannot launch a batch send.',
  sms_manager: 'Builds text campaigns and attests audience consent for batch sends.',
  candidate: 'The candidate’s own login. Sees only their district, only their race.',
};

const STEPS = [
  { key: 'race', label: 'The Race', hint: 'Who is running, and for what' },
  { key: 'turf', label: 'The Turf', hint: 'Which district they are running in' },
  { key: 'roster', label: 'The Roster', hint: 'Everyone who needs an account' },
  { key: 'compliance', label: 'Compliance', hint: 'Contribution limits for filing' },
  { key: 'launch', label: 'Launch', hint: 'Review and start' },
] as const;
type StepKey = (typeof STEPS)[number]['key'];

const EMPTY_DRAFT: LaunchDraft = {
  race: {
    slug: '',
    full_name: '',
    office: '',
    district_label: '',
    party: '',
    election_date: null,
  },
  reach: [],
  roster: [],
  compliance: { office_type: '', limit_cents: null, jurisdiction: 'MO' },
  county_wide_confirmed: false,
};

/** Lowercase-kebab, matching the server's slug pattern. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({
  label,
  hint,
  children,
}) => (
  <div>
    <label className="block text-[11px] font-bold uppercase tracking-wider text-fg-muted mb-1.5">
      {label}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-fg-muted">{hint}</p>}
  </div>
);

const inputCls =
  'w-full bg-bg-raised border border-border-subtle rounded px-3 py-2 text-sm text-fg-primary focus:outline-none focus:ring-2 focus:ring-accent';

/** A finding, styled by severity. Blocks gate the button; warnings never do. */
const FindingRow: React.FC<{ severity: 'block' | 'warn'; message: string }> = ({
  severity,
  message,
}) => (
  <div
    className={`flex items-start gap-2 text-xs leading-relaxed ${
      severity === 'block' ? 'text-status-danger' : 'text-status-warning'
    }`}
  >
    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
    <span>{message}</span>
  </div>
);

// ---------------------------------------------------------------------------
// Scope editor — one row, reused for the reach and for each staffer's turf.
// ---------------------------------------------------------------------------

const ScopeEditor: React.FC<{
  scopes: UserScopeInput[];
  onChange: (next: UserScopeInput[]) => void;
  /** When set, only subregions inside these LDs are offered. */
  limitToLds?: string[];
  emptyLabel: string;
}> = ({ scopes, onChange, limitToLds, emptyLabel }) => {
  const { catalog: regions } = useMapRegions();
  const allowedSubregions = useMemo(
    () =>
      limitToLds && limitToLds.length
        ? regions.keys.filter((k) =>
            limitToLds.includes(String(regions.byKey[k].ld)),
          )
        : regions.keys,
    [regions, limitToLds],
  );
  const allowedLds =
    limitToLds && limitToLds.length ? limitToLds : regions.ldOptions;

  // Nothing to pick from means the tenant's turfs were never loaded. Say so
  // rather than rendering an editor whose every dropdown is empty — a scope
  // row that names no turf is dropped on submit and the campaign silently
  // ends up county-wide, which is the widest setting, not the narrowest.
  if (!allowedSubregions.length && !allowedLds.length) {
    return (
      <p className="text-xs text-fg-muted italic">
        No turfs are loaded for this tenant yet, so a district cannot be picked.
        Contact platform support to load them.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {scopes.length === 0 && (
        <p className="text-xs text-fg-muted italic">{emptyLabel}</p>
      )}
      {scopes.map((scope, i) => (
        <div key={i} className="flex items-center gap-2">
          <select
            className={`${inputCls} max-w-[130px]`}
            value={scope.kind}
            onChange={(e) => {
              const kind = e.target.value as 'ld' | 'subregion';
              const next = [...scopes];
              next[i] =
                kind === 'ld'
                  ? { kind: 'ld', ld: allowedLds[0] }
                  : {
                      kind: 'subregion',
                      ld: String(regions.byKey[allowedSubregions[0]].ld),
                      precincts:
                        regions.byKey[allowedSubregions[0]].precinctsAsStrings,
                    };
              onChange(next);
            }}
          >
            <option value="ld">Whole district</option>
            <option value="subregion">Part of a district</option>
          </select>

          {scope.kind === 'ld' ? (
            <select
              className={`${inputCls} max-w-[150px]`}
              value={String(scope.ld)}
              onChange={(e) => {
                const next = [...scopes];
                next[i] = { kind: 'ld', ld: e.target.value };
                onChange(next);
              }}
            >
              {allowedLds.map((ld) => (
                <option key={ld} value={ld}>
                  LD {ld}
                </option>
              ))}
            </select>
          ) : (
            <select
              className={`${inputCls} flex-1`}
              value={
                allowedSubregions.find(
                  (k) =>
                    String(regions.byKey[k].ld) === String(scope.ld) &&
                    regions.byKey[k].precinctsAsStrings.join(',') ===
                      (scope.precincts || []).map(String).join(','),
                ) || ''
              }
              onChange={(e) => {
                const key = e.target.value;
                const next = [...scopes];
                next[i] = {
                  kind: 'subregion',
                  ld: String(regions.byKey[key].ld),
                  precincts: regions.byKey[key].precinctsAsStrings,
                };
                onChange(next);
              }}
            >
              <option value="">Pick an area…</option>
              {allowedSubregions.map((k) => (
                <option key={k} value={k}>
                  {regions.byKey[k].name} (LD {regions.byKey[k].ld},{' '}
                  {regions.byKey[k].precinctCount} precincts)
                </option>
              ))}
            </select>
          )}

          <button
            type="button"
            onClick={() => onChange(scopes.filter((_, j) => j !== i))}
            className="p-1.5 text-fg-muted hover:text-status-danger"
            aria-label="Remove turf"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() =>
          onChange([...scopes, { kind: 'ld', ld: allowedLds[0] || '12' }])
        }
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-fg hover:underline"
      >
        <Plus className="w-3.5 h-3.5" /> Add turf
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Roster templates — three typical teams, seeded instead of remembered.
// ---------------------------------------------------------------------------

/**
 * The template picker that sits above the roster rows.
 *
 * Everything it needs to be honest about a template is known before the click:
 * which roles this provisioner may grant, and how many seats the tenant has
 * left. So each card states its own cost up front — how many people it adds,
 * what it had to leave out, and whether it fits — rather than seeding ten rows
 * and letting preflight deliver the bad news three steps later.
 *
 * Applying APPENDS. See `applyRosterTemplate` for why replacing would be the
 * wrong trade with hand-typed emails already on screen.
 */
const RosterTemplatePicker: React.FC<{
  roster: LaunchRosterMember[];
  reach: UserScopeInput[];
  roleOptions: RoleOption[];
  seats: SeatSnapshot | null;
  onApply: (roster: LaunchRosterMember[]) => void;
}> = ({ roster, reach, roleOptions, seats, onApply }) => {
  // Templates slice turf out of the candidate's reach, so the picker needs
  // this tenant's catalog. An empty one is not fatal: a `slice` slot falls
  // back to a copy of the reach (see `applyRosterTemplate`).
  const { catalog: regions } = useMapRegions();
  /** A template that doesn't fit the seat count needs a second press. */
  const [pending, setPending] = useState<RosterTemplateId | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const previews = useMemo(
    () =>
      ROSTER_TEMPLATES.map((template) => ({
        template,
        app: applyRosterTemplate({
          template,
          roster,
          reach,
          roleOptions,
          seats,
          regions,
        }),
      })),
    [roster, reach, roleOptions, seats, regions],
  );

  // Until the grant matrix has loaded there is no way to honour "never seed a
  // role you cannot grant", so there is nothing safe to offer. The manual
  // "Add person" button is unaffected.
  if (!roleOptions.some((o) => o.grantable)) return null;

  return (
    <div className="rounded border border-border-base bg-bg-raised p-3">
      <div className="mb-2.5">
        <div className="text-[13px] font-bold text-fg-primary">
          Start from a typical team
          <InfoTip termId="roster_template" compact className="ml-1 align-middle" />
        </div>
        <p className="text-xs text-fg-muted mt-0.5">
          Adds rows with the roles and turf already set. You still type every
          email — and you can edit or remove any row afterwards.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {previews.map(({ template, app }) => {
          const shortfall = app.shortfalls[0];
          const confirming = pending === template.id;
          return (
            <div
              key={template.id}
              className={`rounded border p-2.5 ${
                confirming ? 'border-status-warning' : 'border-border-subtle'
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  // Warn BEFORE seeding: a roster that outruns the tenant's
                  // seats is a `SEATS_EXHAUSTED` block at the Launch step, and
                  // finding that out after ten rows is the whole problem.
                  if (shortfall && !confirming) {
                    setPending(template.id);
                    setNote(null);
                    return;
                  }
                  onApply(app.roster);
                  setPending(null);
                  setNote(
                    `Added ${app.added.length} ${
                      app.added.length === 1 ? 'row' : 'rows'
                    } from ${template.name}. Fill in the emails.`,
                  );
                }}
                disabled={app.added.length === 0}
                className="w-full text-left disabled:opacity-50"
              >
                <span className="flex items-center gap-1.5 text-[13px] font-bold text-fg-primary">
                  <Users className="w-3.5 h-3.5 text-fg-muted shrink-0" />
                  {template.name}
                </span>
                <span className="block text-xs text-fg-muted mt-1 leading-snug">
                  {template.blurb}
                </span>
                <span className="block text-[11px] font-semibold text-accent-fg mt-1.5">
                  {app.added.length === 0
                    ? 'Nothing left to add'
                    : `Adds ${app.added.length} ${
                        app.added.length === 1 ? 'person' : 'people'
                      }`}
                </span>
              </button>

              {app.skipped
                .filter((s) => s.reason === 'not_grantable')
                .map((s) => (
                  <p key={s.role} className="text-[11px] text-fg-muted mt-1.5 leading-snug">
                    Leaves out the {s.label.toLowerCase()} — your account
                    can&rsquo;t grant {s.role.replace(/_/g, ' ')}. Ask an admin.
                  </p>
                ))}

              {shortfall && (
                <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-status-warning leading-snug">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>
                    Needs {describeShortfalls(app.shortfalls)} than this tenant
                    has left.
                    {confirming
                      ? ' Press again to add anyway, or pick a smaller team.'
                      : ''}
                  </span>
                </div>
              )}

              {confirming && (
                <button
                  type="button"
                  onClick={() => setPending(null)}
                  className="mt-1.5 text-[11px] font-semibold text-fg-muted hover:text-fg-primary"
                >
                  Cancel
                </button>
              )}
            </div>
          );
        })}
      </div>

      {note && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-fg-secondary">
          <Check className="w-3.5 h-3.5 text-status-success shrink-0" />
          {note}
        </p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

export const CampaignSetupView: React.FC = () => {
  const [plans, setPlans] = useState<LaunchPlan[]>([]);
  const [planId, setPlanId] = useState<number | null>(null);
  const [draft, setDraft] = useState<LaunchDraft>(EMPTY_DRAFT);
  const [step, setStep] = useState<StepKey>('race');
  const [report, setReport] = useState<PreflightReport | null>(null);
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  /**
   * Seat limits for this tenant, so the roster step can warn about
   * `SEATS_EXHAUSTED` while someone is still choosing a team rather than at
   * the Launch step. Null means we could not read them — an older server, or
   * a caller the endpoint declines — in which case no seat claim is made.
   */
  const [seats, setSeats] = useState<SeatSnapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LaunchCommitResult | null>(null);
  const [routes, setRoutes] = useState<StarterRoute[] | null>(null);

  /**
   * Stable per plan, NOT per click. If a commit times out and the user
   * retries, this is what makes the server recognize the retry as the same
   * attempt instead of creating a second roster.
   */
  const [idemKey, setIdemKey] = useState<string>('');

  const refreshPlans = useCallback(async () => {
    try {
      setPlans(await listLaunchPlans());
    } catch {
      setError('Could not load campaigns.');
    }
  }, []);

  useEffect(() => {
    refreshPlans();
    fetchRoleOptions().then(setRoleOptions).catch(() => setRoleOptions([]));
    fetchSeats().then(setSeats).catch(() => setSeats(null));
  }, [refreshPlans]);

  /** Save the draft, then ask the server whether it is ready. */
  const syncAndCheck = useCallback(
    async (next?: LaunchDraft) => {
      if (planId == null) return;
      setBusy(true);
      setError(null);
      try {
        await updateLaunchPlan(planId, next ?? draft);
        setReport(await preflightLaunchPlan(planId));
      } catch (e: any) {
        setError(errorText(e, 'Could not save.'));
      } finally {
        setBusy(false);
      }
    },
    [planId, draft],
  );

  const startNew = async () => {
    setBusy(true);
    setError(null);
    try {
      const plan = await createLaunchPlan(EMPTY_DRAFT, 'New campaign');
      setPlanId(plan.id);
      setDraft(EMPTY_DRAFT);
      setStep('race');
      setReport(null);
      setResult(null);
      setIdemKey(`launch-${plan.id}-${Date.now()}`);
      await refreshPlans();
    } catch (e: any) {
      setError(errorText(e, 'Could not start a new campaign.'));
    } finally {
      setBusy(false);
    }
  };

  const openPlan = (plan: LaunchPlan) => {
    setPlanId(plan.id);
    setDraft(plan.draft ?? EMPTY_DRAFT);
    setStep('race');
    setResult(null);
    setReport(null);
    setIdemKey(`launch-${plan.id}-${Date.now()}`);
  };

  const commit = async () => {
    if (planId == null) return;
    setBusy(true);
    setError(null);
    try {
      await updateLaunchPlan(planId, draft);
      setResult(await commitLaunchPlan(planId, idemKey));
      await refreshPlans();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(errorText(e, 'Could not start the campaign.'));
      if (detail?.findings) {
        setReport((r) => (r ? { ...r, ready: false, findings: detail.findings } : r));
      }
    } finally {
      setBusy(false);
    }
  };

  // Findings indexed by subject, so a row can show its own problems.
  const findingsFor = useCallback(
    (subject: string) =>
      (report?.findings ?? []).filter((f) => f.subject === subject),
    [report],
  );
  const generalFindings = useMemo(
    () =>
      (report?.findings ?? []).filter(
        (f) => !f.subject || !draft.roster.some((m) => m.email === f.subject),
      ),
    [report, draft.roster],
  );

  const reachLds = useMemo(
    () => draft.reach.map((s) => String(s.ld)),
    [draft.reach],
  );

  const patch = (partial: Partial<LaunchDraft>) =>
    setDraft((d) => ({ ...d, ...partial }));

  // -------------------------------------------------------------------------
  // Plan list
  // -------------------------------------------------------------------------
  if (planId == null) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-fg-primary">Campaign Setup</h1>
            <p className="text-sm text-fg-muted mt-1">
              Add a candidate and their whole team at once. Nothing is created
              until you press Start.
            </p>
          </div>
          <button
            onClick={startNew}
            disabled={busy}
            className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white text-sm font-semibold px-4 py-2 rounded disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> New campaign
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded border border-status-danger text-status-danger text-sm">
            {error}
          </div>
        )}

        {plans.length === 0 ? (
          <div className="border border-border-base rounded p-8 text-center text-fg-muted">
            <p className="text-sm">No campaigns yet.</p>
          </div>
        ) : (
          <div className="border border-border-base rounded divide-y divide-border-base">
            {plans.map((p) => (
              <button
                key={p.id}
                onClick={() => openPlan(p)}
                className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-bg-elevated"
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-fg-primary truncate">
                    {p.name}
                  </div>
                  <div className="text-xs text-fg-muted">
                    {p.draft?.roster.length ?? 0} people ·{' '}
                    {new Date(p.created_at).toLocaleDateString()}
                  </div>
                </div>
                <span
                  className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${
                    p.status === 'committed'
                      ? 'text-status-success border-status-success'
                      : p.status === 'abandoned'
                        ? 'text-fg-muted border-border-subtle'
                        : 'text-accent-fg border-accent'
                  }`}
                >
                  {p.status === 'committed' ? 'Started' : p.status}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Success screen — the invite links, shown once.
  // -------------------------------------------------------------------------
  if (result) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-full bg-status-success grid place-items-center">
            <Check className="w-5 h-5 text-bg-base" />
          </div>
          <h1 className="text-2xl font-bold text-fg-primary">
            {draft.race.full_name}&rsquo;s campaign is live
          </h1>
        </div>
        <p className="text-sm text-fg-muted mb-6">
          {result.created_user_ids.length} accounts created. Send each person
          their link — <strong className="text-fg-secondary">this is the only
          time these are shown</strong>, and each one works once.
          <InfoTip termId="invite_link" compact className="ml-1 align-middle" />
        </p>

        {/* The copy-link list and the CSV below are shared with Admin →
            Users' "chase the unredeemed invites". One renderer, one URL
            formatter: the last time these were two copies, the CSV's
            hand-rolled `/invite/<token>` path 404'd for every recipient. */}
        <InviteLinkList invites={result.invites} className="mb-6" />

        <div className="mb-6 p-4 rounded border border-border-base bg-bg-raised">
          <div className="text-sm font-semibold text-fg-primary mb-1">
            Give the team something to knock
          </div>
          <p className="text-xs text-fg-muted mb-3">
            Drafts up to three walking routes from the highest-opportunity
            precincts in this district, using your real voter roll. They start
            as drafts so a coordinator reviews them first.
          </p>
          {routes === null ? (
            <button
              onClick={async () => {
                if (planId == null) return;
                setBusy(true);
                setError(null);
                try {
                  const r = await buildStarterRoutes(planId);
                  setRoutes(r.routes);
                } catch (e: any) {
                  setError(errorText(e, 'Could not draft starter routes.'));
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              className="inline-flex items-center gap-2 text-xs font-semibold border border-border-subtle rounded px-3 py-2 text-fg-secondary hover:bg-bg-elevated disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Route className="w-3.5 h-3.5" />
              )}
              Draft starter routes
            </button>
          ) : routes.length === 0 ? (
            <p className="text-xs text-fg-muted">
              Starter routes were already drafted for this campaign.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {routes.map((r) => (
                <li key={r.id} className="flex items-center gap-2 text-xs text-fg-secondary">
                  <Check className="w-3.5 h-3.5 text-status-success shrink-0" />
                  <span className="truncate">
                    {r.name} &middot; {r.door_count} doors
                    {r.est_hours ? ` · ~${r.est_hours.toFixed(1)}h` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 rounded border border-status-danger text-status-danger text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <InviteCsvButton
            invites={result.invites}
            filename={`invites-${draft.race.slug}.csv`}
          />
          <button
            onClick={() => {
              setPlanId(null);
              setResult(null);
            }}
            className="text-sm font-semibold bg-accent hover:bg-accent-hover text-white rounded px-4 py-2"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Wizard
  // -------------------------------------------------------------------------
  const stepIndex = STEPS.findIndex((s) => s.key === step);
  const blocking = (report?.findings ?? []).filter((f) => f.severity === 'block');

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => setPlanId(null)}
          className="p-1.5 text-fg-muted hover:text-fg-primary"
          aria-label="Back to campaigns"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-xl font-bold text-fg-primary">
          {draft.race.full_name || 'New campaign'}
        </h1>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-fg-muted" />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
        {/* Step rail */}
        <nav className="space-y-1">
          {STEPS.map((s, i) => {
            const done = i < stepIndex;
            const here = s.key === step;
            return (
              <button
                key={s.key}
                onClick={() => {
                  setStep(s.key);
                  syncAndCheck();
                }}
                className={`w-full flex items-start gap-3 px-3 py-2.5 rounded text-left border-l-2 ${
                  here
                    ? 'border-accent bg-bg-raised'
                    : 'border-transparent hover:bg-bg-elevated'
                }`}
              >
                <span
                  className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold shrink-0 mt-0.5 ${
                    done
                      ? 'bg-status-success text-bg-base'
                      : here
                        ? 'border border-accent text-accent-fg'
                        : 'border border-border-subtle text-fg-muted'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-fg-secondary">
                    {s.label}
                  </span>
                  <span className="block text-[11px] text-fg-muted leading-tight">
                    {s.hint}
                  </span>
                </span>
              </button>
            );
          })}
        </nav>

        {/* Panel */}
        <div className="min-w-0">
          {error && (
            <div className="mb-4 p-3 rounded border border-status-danger text-status-danger text-sm">
              {error}
            </div>
          )}

          {step === 'race' && (
            <section className="space-y-4">
              <h2 className="text-base font-bold text-fg-primary">
                Who is running?
                <InfoTip termId="candidate_login" compact className="ml-1 align-middle" />
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full name">
                  <input
                    className={inputCls}
                    value={draft.race.full_name}
                    onChange={(e) =>
                      patch({
                        race: {
                          ...draft.race,
                          full_name: e.target.value,
                          slug: draft.race.slug || slugify(e.target.value),
                        },
                      })
                    }
                    placeholder="Dana Whitfield"
                  />
                </Field>
                <Field
                  label="Office"
                  hint="How it appears on the ballot."
                >
                  <input
                    className={inputCls}
                    value={draft.race.office}
                    onChange={(e) =>
                      patch({ race: { ...draft.race, office: e.target.value } })
                    }
                    placeholder="State Representative"
                  />
                </Field>
                <Field
                  label="Identifier"
                  hint="Permanent. Never reuse one from a past candidate — it would re-attribute their knock history."
                >
                  <input
                    className={`${inputCls} font-mono text-xs`}
                    value={draft.race.slug}
                    onChange={(e) =>
                      patch({ race: { ...draft.race, slug: slugify(e.target.value) } })
                    }
                    placeholder="dana-whitfield"
                  />
                </Field>
                <Field label="District label" hint="Display only — the real turf is the next step.">
                  <input
                    className={inputCls}
                    value={draft.race.district_label ?? ''}
                    onChange={(e) =>
                      patch({
                        race: { ...draft.race, district_label: e.target.value },
                      })
                    }
                    placeholder="LD 12"
                  />
                </Field>
                <Field label="Party">
                  <input
                    className={inputCls}
                    value={draft.race.party ?? ''}
                    onChange={(e) =>
                      patch({ race: { ...draft.race, party: e.target.value } })
                    }
                    placeholder="Republican"
                  />
                </Field>
                <Field label="Election date">
                  <input
                    type="date"
                    className={inputCls}
                    value={draft.race.election_date ?? ''}
                    onChange={(e) =>
                      patch({
                        race: { ...draft.race, election_date: e.target.value || null },
                      })
                    }
                  />
                </Field>
              </div>
            </section>
          )}

          {step === 'turf' && (
            <section className="space-y-4">
              <h2 className="text-base font-bold text-fg-primary">
                Which district are they running in?
                <InfoTip termId="region_scope" compact className="ml-1 align-middle" />
              </h2>
              <p className="text-sm text-fg-muted">
                Everyone on the campaign works inside this. Staff can be given a
                smaller piece of it, never a larger one.
              </p>

              <ScopeEditor
                scopes={draft.reach}
                onChange={(reach) => patch({ reach })}
                emptyLabel="No district set yet."
              />

              <div className="flex items-start gap-1 pt-2">
                <label className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={draft.county_wide_confirmed}
                    onChange={(e) =>
                      patch({ county_wide_confirmed: e.target.checked })
                    }
                  />
                  <span className="text-xs text-fg-secondary leading-relaxed">
                    This is a <strong>county-wide</strong> office (Sheriff,
                    Assessor).
                    <span className="block text-fg-muted">
                      Leaving the district blank gives this campaign the entire
                      county roll, so it has to be deliberate.
                    </span>
                  </span>
                </label>
                <InfoTip termId="county_wide_office" compact />
              </div>

              {report && (
                <div className="mt-4 p-4 rounded border border-border-base bg-bg-raised">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-fg-muted mb-1">
                    This turf covers
                  </div>
                  {report.reach.tenant_global ? (
                    <div className="text-sm text-status-warning font-semibold">
                      The entire county
                    </div>
                  ) : (
                    <div className="text-sm text-fg-primary">
                      <span className="text-xl font-bold tabular-nums">
                        {report.reach.voter_count.toLocaleString()}
                      </span>{' '}
                      voters across {report.reach.precinct_count} precincts
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={() => syncAndCheck()}
                className="text-xs font-semibold text-accent-fg hover:underline"
              >
                Recalculate
              </button>
            </section>
          )}

          {step === 'roster' && (
            <section className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-base font-bold text-fg-primary">
                    Who&rsquo;s on the team?
                    <InfoTip termId="user_role" compact className="ml-1 align-middle" />
                  </h2>
                  <p className="text-sm text-fg-muted mt-1">
                    Everyone here gets an account and an invite link when you
                    start the campaign.
                    <InfoTip termId="invite_link" compact className="ml-1 align-middle" />
                  </p>
                </div>
                <button
                  onClick={() => {
                    const member: LaunchRosterMember = {
                      email: '',
                      full_name: '',
                      role: draft.roster.some((m) => m.relation === 'candidate')
                        ? 'canvasser'
                        : 'candidate',
                      relation: draft.roster.some((m) => m.relation === 'candidate')
                        ? 'staff'
                        : 'candidate',
                      scopes: [],
                      can_provision: false,
                    };
                    patch({ roster: [...draft.roster, member] });
                  }}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border-subtle rounded px-3 py-2 text-fg-secondary hover:bg-bg-elevated shrink-0"
                >
                  <Plus className="w-3.5 h-3.5" /> Add person
                </button>
              </div>

              <RosterTemplatePicker
                roster={draft.roster}
                reach={draft.reach}
                roleOptions={roleOptions}
                seats={seats}
                onApply={(roster) => patch({ roster })}
              />

              <div className="space-y-3">
                {draft.roster.map((m, i) => {
                  const rowFindings = findingsFor(m.email);
                  const bad = rowFindings.some((f) => f.severity === 'block');
                  return (
                    <div
                      key={i}
                      className={`p-4 rounded border ${
                        bad ? 'border-status-danger' : 'border-border-base'
                      } bg-bg-raised space-y-3`}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Name">
                          <input
                            className={inputCls}
                            value={m.full_name ?? ''}
                            onChange={(e) => {
                              const roster = [...draft.roster];
                              roster[i] = { ...m, full_name: e.target.value };
                              patch({ roster });
                            }}
                          />
                        </Field>
                        <Field label="Email">
                          <input
                            className={inputCls}
                            value={m.email}
                            onChange={(e) => {
                              const roster = [...draft.roster];
                              roster[i] = { ...m, email: e.target.value };
                              patch({ roster });
                            }}
                            placeholder="name@campaign.org"
                          />
                        </Field>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Role" hint={ROLE_BLURB[m.role]}>
                          <select
                            className={inputCls}
                            value={m.role}
                            onChange={(e) => {
                              const role = e.target.value;
                              const roster = [...draft.roster];
                              roster[i] = {
                                ...m,
                                role,
                                relation: role === 'candidate' ? 'candidate' : 'staff',
                                scopes: role === 'candidate' ? [] : m.scopes,
                              };
                              patch({ roster });
                            }}
                          >
                            {roleOptions.map((o) => (
                              <option
                                key={o.value}
                                value={o.value}
                                disabled={!o.grantable}
                              >
                                {o.value.replace(/_/g, ' ')}
                                {o.grantable ? '' : ' — needs an admin'}
                              </option>
                            ))}
                          </select>
                        </Field>

                        <div>
                          <label className="block text-[11px] font-bold uppercase tracking-wider text-fg-muted mb-1.5">
                            Turf
                          </label>
                          {m.relation === 'candidate' ? (
                            <p className="text-xs text-fg-muted italic">
                              The candidate gets the campaign&rsquo;s whole
                              district automatically.
                            </p>
                          ) : (
                            <ScopeEditor
                              scopes={m.scopes}
                              limitToLds={reachLds}
                              onChange={(scopes) => {
                                const roster = [...draft.roster];
                                roster[i] = { ...m, scopes };
                                patch({ roster });
                              }}
                              emptyLabel="Inherits the whole district."
                            />
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-3 pt-1">
                        <label className="flex items-center gap-2 text-xs text-fg-secondary">
                          <input
                            type="checkbox"
                            checked={m.can_provision}
                            onChange={(e) => {
                              const roster = [...draft.roster];
                              roster[i] = { ...m, can_provision: e.target.checked };
                              patch({ roster });
                            }}
                          />
                          Can add people to the campaign
                        </label>
                        <button
                          onClick={() =>
                            patch({ roster: draft.roster.filter((_, j) => j !== i) })
                          }
                          className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-status-danger"
                        >
                          <X className="w-3.5 h-3.5" /> Remove
                        </button>
                      </div>

                      {rowFindings.length > 0 && (
                        <div className="space-y-1.5 pt-1 border-t border-border-base">
                          {rowFindings.map((f, k) => (
                            <FindingRow key={k} severity={f.severity} message={f.message} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {draft.roster.length === 0 && (
                  <p className="text-sm text-fg-muted italic">
                    Nobody added yet. Start with the candidate.
                  </p>
                )}
              </div>
            </section>
          )}

          {step === 'compliance' && (
            <section className="space-y-4">
              <h2 className="text-base font-bold text-fg-primary">
                Contribution limits
              </h2>
              <p className="text-sm text-fg-muted">
                Used by the Compliance view to check donations and build the
                filing export.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Office type" hint="Groups races that share a limit.">
                  <input
                    className={inputCls}
                    value={draft.compliance.office_type ?? ''}
                    onChange={(e) =>
                      patch({
                        compliance: {
                          ...draft.compliance,
                          office_type: e.target.value,
                        },
                      })
                    }
                    placeholder="State Representative"
                  />
                </Field>
                <Field
                  label="Limit per election (dollars)"
                  hint="Leave blank for no limit — correct for county central-committee races."
                >
                  <input
                    type="number"
                    min={0}
                    className={inputCls}
                    value={
                      draft.compliance.limit_cents == null
                        ? ''
                        : draft.compliance.limit_cents / 100
                    }
                    onChange={(e) =>
                      patch({
                        compliance: {
                          ...draft.compliance,
                          limit_cents:
                            e.target.value === ''
                              ? null
                              : Math.round(Number(e.target.value) * 100),
                        },
                      })
                    }
                    placeholder="2600"
                  />
                </Field>
              </div>
            </section>
          )}

          {step === 'launch' && (
            <section className="space-y-4">
              <h2 className="text-base font-bold text-fg-primary">
                Ready to start {draft.race.full_name || 'this campaign'}?
              </h2>
              <p className="text-sm text-fg-muted">
                Everything below happens in one step. If any part fails, nothing
                is created and your draft is untouched.
              </p>

              {report && (
                <div className="space-y-2 p-4 rounded border border-border-base bg-bg-raised">
                  <div className="flex items-center gap-2 text-sm text-fg-secondary">
                    <Check className="w-4 h-4 text-status-success" />
                    <span>
                      <strong className="text-fg-primary">
                        {report.account_count} accounts
                      </strong>{' '}
                      and {report.invite_count} invite links
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-fg-secondary">
                    <Check className="w-4 h-4 text-status-success" />
                    <span>
                      <strong className="text-fg-primary">
                        {report.scope_row_count} turf assignments
                      </strong>
                      {!report.reach.tenant_global && (
                        <>
                          {' '}
                          inside {report.reach.precinct_count} precincts (
                          {report.reach.voter_count.toLocaleString()} voters)
                        </>
                      )}
                    </span>
                  </div>
                  {report.seats.map((s) => (
                    <div
                      key={s.seat_class}
                      className="flex items-center gap-2 text-sm text-fg-secondary"
                    >
                      <Check className="w-4 h-4 text-status-success" />
                      <span>
                        {s.needed} {s.seat_class} seat{s.needed === 1 ? '' : 's'} —{' '}
                        {s.unlimited ? 'unlimited available' : `${s.remaining} remaining`}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {generalFindings.length > 0 && (
                <div className="space-y-2 p-4 rounded border border-border-base">
                  {generalFindings.map((f, i) => (
                    <FindingRow key={i} severity={f.severity} message={f.message} />
                  ))}
                </div>
              )}

              <p className="text-xs text-fg-muted">
                Everyone gets an invite link to set their own password. Nothing
                is emailed automatically — you&rsquo;ll get the links to send.
              </p>
            </section>
          )}
        </div>
      </div>

      {/* Readiness footer */}
      <div className="mt-6 pt-4 border-t border-border-base flex flex-wrap items-center gap-3">
        {report && (
          <>
            <span className="text-xs text-fg-muted">
              <strong className="text-fg-primary tabular-nums">
                {report.account_count}
              </strong>{' '}
              accounts
            </span>
            <span className="text-xs text-fg-muted">
              <strong className="text-fg-primary tabular-nums">
                {report.scope_row_count}
              </strong>{' '}
              turf assignments
            </span>
            {blocking.length > 0 && (
              <span className="text-xs font-semibold text-status-danger">
                {blocking.length} thing{blocking.length === 1 ? '' : 's'} to fix
              </span>
            )}
          </>
        )}
        <span className="flex-1" />

        {stepIndex > 0 && (
          <button
            onClick={() => setStep(STEPS[stepIndex - 1].key)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold border border-border-subtle rounded px-3 py-2 text-fg-secondary hover:bg-bg-elevated"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        )}
        <button
          onClick={() => syncAndCheck()}
          disabled={busy}
          className="text-sm font-semibold border border-border-subtle rounded px-3 py-2 text-fg-secondary hover:bg-bg-elevated disabled:opacity-50"
        >
          Save draft
        </button>
        {step !== 'launch' ? (
          <button
            onClick={() => {
              setStep(STEPS[stepIndex + 1].key);
              syncAndCheck();
            }}
            className="inline-flex items-center gap-1.5 text-sm font-semibold bg-accent hover:bg-accent-hover text-white rounded px-4 py-2"
          >
            Next <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={commit}
            disabled={busy || !report?.ready}
            title={
              report?.ready
                ? undefined
                : 'Fix the highlighted problems before starting.'
            }
            className="inline-flex items-center gap-2 text-sm font-bold bg-accent hover:bg-accent-hover text-white rounded px-5 py-2 disabled:bg-bg-elevated disabled:text-fg-muted"
          >
            <Rocket className="w-4 h-4" /> Start Campaign
          </button>
        )}
      </div>
    </div>
  );
};

export default CampaignSetupView;
